import React from 'react';
import { X, Thermometer } from 'lucide-react';
import { useGetZoneSensorsQuery } from '../../services/zoneApi';
import SensorAssignment from './SensorAssignment';
import { formatSensorSerial } from '../../utils/sensorUtils';
import type { Sensor } from '../../types';

interface ZoneSensorsProps {
  zoneId: number;
  sensors: Sensor[];
  onAssign: (zoneId: number, sensorId: number) => void;
  onRemove: (zoneId: number, sensorId: number) => void;
  zonesLoading?: boolean;
}

export default function ZoneSensors({ zoneId, sensors, onAssign, onRemove, zonesLoading }: ZoneSensorsProps) {
  const { data: zoneSensors, isLoading } = useGetZoneSensorsQuery(zoneId);
  
  const [selectedSensorIds, setSelectedSensorIds] = React.useState<number[]>([]);

  const handleCheckboxChange = (sensorId: number) => {
    setSelectedSensorIds(prev =>
      prev.includes(sensorId)
        ? prev.filter(id => id !== sensorId)
        : [...prev, sensorId]
    );
  };

  const handleDeleteSelected = () => {
    selectedSensorIds.forEach(sensorId => onRemove(zoneId, sensorId));
    setSelectedSensorIds([]);
  };

  // Filter sensors to only show ones assigned to this zone
  const assignedSensors = React.useMemo(() => {
    if (!zoneSensors || !sensors) return [];
    return sensors.filter(sensor => {
      const sensorIdNum = Number(sensor.id);
      const sensorSerialNum = Number(sensor.serialNo);
      const sensorData = zoneSensors.find(s => Number(s.sensor_id) === sensorIdNum || Number(s.sensor_serialNo) === sensorSerialNum);
      if (!sensorData || !sensorData.zone_ids) return false;
      let zoneIds: number[] = [];
      if (typeof sensorData.zone_ids === 'string') {
        zoneIds = (sensorData.zone_ids as string).split(',').map(Number);
      } else if (Array.isArray(sensorData.zone_ids)) {
        zoneIds = (sensorData.zone_ids as (string | number)[]).map(z => Number(z));
      } else {
        zoneIds = [];
      }
      return zoneIds.includes(Number(zoneId));
    });
  },
    [sensors, zoneSensors, zoneId]
  );

  const isWaitingForData =
    isLoading ||
    zonesLoading ||
    !zoneSensors ||
    (Array.isArray(zoneSensors) && zoneSensors.length === 0 && (isLoading || zonesLoading));

  if (isWaitingForData) {
    return (
      <div className="p-4">
        <div className="py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
          <span className="inline-block w-5 h-5 align-middle animate-spin border-2 border-blue-400 border-t-transparent rounded-full mr-2"></span>
          Updating sensors...
        </div>
      </div>
    );
  }

  const showNoSensorsMessage =
    !isLoading &&
    !zonesLoading &&
    zoneSensors &&
    assignedSensors.length === 0;

  return (
    <div className="p-2 sm:p-4 w-full">
      <div className="space-y-2">
        <SensorAssignment
          zoneId={zoneId}
          sensors={sensors}
          onAssign={onAssign}
        />
        {showNoSensorsMessage ? (
          <div className="py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
            No sensors assigned to this zone
          </div>
        ) : (
          <div className="mt-3">
            <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-100/50 dark:bg-gray-800/50">
                <tr>
                  <th scope="col" className="px-2 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                    Sensor Name
                  </th>
                  <th scope="col" className="hidden sm:table-cell px-2 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                    Serial Number
                  </th>
                  <th scope="col" className="px-2 sm:px-6 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 w-16">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                {assignedSensors.map((sensor) => (
                  <tr key={sensor.id} className="hover:bg-gray-50 dark:hover:bg-gray-600/50">
                    <td className="px-2 sm:px-6 py-3">
                      <div className="flex items-center">
                        <Thermometer className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {sensor.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 sm:hidden">
                          {formatSensorSerial(sensor.serialNo, sensor.type)}
                        </span>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-2 sm:px-6 py-3 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatSensorSerial(sensor.serialNo, sensor.type)}
                      </span>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap text-right flex items-center justify-end space-x-2">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded mr-2"
                        checked={selectedSensorIds.includes(sensor.id)}
                        onChange={() => handleCheckboxChange(sensor.id)}
                        aria-label="Select sensor for bulk delete"
                      />
                      <button
                        onClick={() => onRemove(zoneId, sensor.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                        title="Remove sensor from zone"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {assignedSensors.length > 0 && (
              <div className="flex justify-end mt-4">
                <button
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                  onClick={handleDeleteSelected}
                  disabled={selectedSensorIds.length === 0}
                >
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}